import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { 
    FaSearch, 
    FaCopy, 
    FaFileExcel, 
    FaPrint, 
    FaFileAlt,
    FaArrowUp,
    FaArrowDown
} from 'react-icons/fa';

const AuditEngineerChallanList = () => {
    const [challans, setChallans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('interested'); // 'interested', 'agreement', 'fee'
    
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const nodesToFetch = [
            'ekamwatapzp/EngineerRegistrations',
            'ekamwatapzp/EngineerLotteryRegistrations'
        ];

        let completedNodes = 0;
        let allFetchedChallans = [];

        nodesToFetch.forEach(nodePath => {
            const nodeRef = ref(db, nodePath);
            onValue(nodeRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    Object.entries(data).forEach(([key, user]) => {
                        const userName = `${user.surname || ''} ${user.firstName || ''} ${user.middleName || ''}`.trim() || user.userName || user.societyName || "वापरकर्ता";
                        const mobile = user.whatsappNo || user.mobile || user.chairmanWhatsapp || "-";
                        const taluka = user.taluka || "N/A";
                        const sourceNode = nodePath;

                        // Tab 1: Interested Works (work_payment node)
                        const workPayments = user.work_payment;
                        if (workPayments) {
                            Object.entries(workPayments).forEach(([paymentId, payment]) => {
                                allFetchedChallans.push({
                                    id: `${key}_${paymentId}`,
                                    userKey: key,
                                    paymentId: paymentId,
                                    userName,
                                    mobile,
                                    taluka,
                                    date: payment.date || payment.selectionTimestamp || "N/A",
                                    status: payment.status || "Approved",
                                    remarks: payment.remarks || "",
                                    type: 'interested',
                                    sourceNode,
                                    amount: payment.paidAmount || "250",
                                    receiptId: payment.receiptId || "N/A"
                                });
                            });
                        }

                        // Tab 2 & 3: Payments Node (Security Deposit / Registration Fee)
                        const payments = user.payments;
                        if (payments) {
                            Object.entries(payments).forEach(([paymentType, payment]) => {
                                let type = '';
                                if (paymentType === 'Security Deposit invoice') type = 'agreement';
                                else if (paymentType === 'Registration invoice') type = 'fee';

                                if (type) {
                                    allFetchedChallans.push({
                                        id: `${key}_${paymentType}`,
                                        userKey: key,
                                        paymentId: paymentType,
                                        userName,
                                        mobile,
                                        taluka,
                                        date: payment.date || "N/A",
                                        status: payment.status || "Approved",
                                        remarks: payment.remarks || "",
                                        type,
                                        sourceNode,
                                        amount: payment.paidAmount || (type === 'agreement' ? "2000" : "400"),
                                        receiptId: payment.receiptId || "N/A"
                                    });
                                }
                            });
                        }
                    });
                }

                completedNodes++;
                if (completedNodes === nodesToFetch.length) {
                    const sorted = allFetchedChallans.sort((a, b) => {
                        const dateA = new Date(a.date);
                        const dateB = new Date(b.date);
                        return dateB - dateA;
                    });
                    setChallans(sorted);
                    setLoading(false);
                }
            });
        });

        return () => unsubscribe();
    }, [navigate]);

    const activeData = challans.filter(item => item.type === activeTab);

    const filteredData = activeData.filter(item => {
        const searchStr = searchTerm.toLowerCase();
        return (
            item.userName.toLowerCase().includes(searchStr) ||
            item.mobile.toLowerCase().includes(searchStr) ||
            item.taluka.toLowerCase().includes(searchStr) ||
            (item.receiptId && item.receiptId.toLowerCase().includes(searchStr))
        );
    });

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === "N/A") return "-";
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            }).toUpperCase();
        } catch (e) {
            return dateStr;
        }
    };

    const handleActionClick = (challan) => {
        // Redirection to dedicated detail page
        navigate(`/audit/challan-details/engineer/${challan.userKey}/${challan.paymentId}/${challan.type}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h1 className="text-xl md:text-2xl font-normal text-gray-800">वर्क चलन</h1>
                <div className="text-[11px] md:text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400 font-bold">वर्क चलन</span>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden min-h-[600px]">
                <div className="border-b border-gray-100 px-6 pt-6 flex flex-wrap gap-4 md:gap-8 relative">
                    <button 
                        onClick={() => setActiveTab('interested')}
                        className={`pb-4 text-xs md:text-sm font-bold transition-all border-b-2 ${activeTab === 'interested' ? 'text-blue-600 border-blue-600' : 'text-[#2a59e8] border-transparent hover:text-blue-800'}`}
                    >
                        कामामध्ये इंटरेस्टेड दाखवलेले चलन
                    </button>
                    <button 
                        onClick={() => setActiveTab('agreement')}
                        className={`pb-4 text-xs md:text-sm font-bold transition-all border-b-2 ${activeTab === 'agreement' ? 'text-blue-600 border-blue-600' : 'text-[#2a59e8] border-transparent hover:text-blue-800'}`}
                    >
                        प्रिंट करारपत्रक
                    </button>
                    <button 
                        onClick={() => setActiveTab('fee')}
                        className={`pb-4 text-xs md:text-sm font-bold transition-all border-b-2 ${activeTab === 'fee' ? 'text-blue-600 border-blue-600' : 'text-[#2a59e8] border-transparent hover:text-blue-800'}`}
                    >
                        वर्क / निविदा फॉर्म फी चलन
                    </button>
                    
                    <div className="md:absolute md:right-6 md:top-6 pb-4 flex items-center gap-2 text-[11px] md:text-xs font-bold text-gray-500">
                        <FaFileAlt className="text-gray-400" />
                        चलन अपलोड केले
                    </div>
                </div>

                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-1 self-start">
                        <button className="px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-normal shadow-sm">Copy</button>
                        <button className="px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-normal shadow-sm">Excel</button>
                        <button className="px-6 py-2 bg-[#6c757d] text-white rounded text-sm font-normal shadow-sm">Print</button>
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <span className="text-sm font-normal text-gray-500">Search:</span>
                        <input 
                            type="text" 
                            className="flex-grow md:w-64 px-2 py-1.5 border border-gray-200 rounded text-sm outline-none transition bg-white"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-y border-gray-200">
                            <tr>
                                <th className="px-5 py-3 text-sm font-bold text-gray-800 border-r w-16">크. <span className="inline-flex flex-col ml-1 opacity-20"><FaArrowUp className="text-[7px]"/><FaArrowDown className="text-[7px]"/></span></th>
                                <th className="px-5 py-3 text-sm font-bold text-gray-800 border-r">अर्जादाराचे नाव <span className="inline-flex flex-col ml-1 opacity-20"><FaArrowUp className="text-[7px]"/><FaArrowDown className="text-[7px]"/></span></th>
                                <th className="px-5 py-3 text-sm font-bold text-gray-800 border-r text-center">तालुका <span className="inline-flex flex-col ml-1 opacity-20"><FaArrowUp className="text-[7px]"/><FaArrowDown className="text-[7px]"/></span></th>
                                <th className="px-5 py-3 text-sm font-bold text-gray-800 border-r">नोंदणी दिनांक <span className="inline-flex flex-col ml-1 opacity-20"><FaArrowUp className="text-[7px]"/><FaArrowDown className="text-[7px]"/></span></th>
                                <th className="px-5 py-3 text-sm font-bold text-gray-800 border-r text-center">चलन स्टेटस <span className="inline-flex flex-col ml-1 opacity-20"><FaArrowUp className="text-[7px]"/><FaArrowDown className="text-[7px]"/></span></th>
                                <th className="px-5 py-3 text-sm font-bold text-gray-800 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 italic">
                            {filteredData.map((item, index) => (
                                <tr key={item.id} className="hover:bg-gray-50 transition-all font-normal text-gray-800">
                                    <td className="px-5 py-4 border-r text-sm font-normal text-center">{index + 1}</td>
                                    <td className="px-5 py-4 border-r text-sm">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-gray-800">{item.userName}</span>
                                            <span className="text-xs font-normal text-gray-600 mt-0.5">{item.mobile}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4 border-r text-center text-sm font-normal">{item.taluka}</td>
                                    <td className="px-5 py-4 border-r text-sm font-normal text-gray-800">{formatDate(item.date)}</td>
                                    <td className="px-5 py-4 border-r text-center">
                                        <span className={`font-bold text-sm ${
                                            item.status === 'Rejected' ? 'text-red-500' : 
                                            item.status === 'Pending' ? 'text-amber-500' : 'text-[#198754]'
                                        }`}>
                                            {item.status || 'Approved'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-center">
                                        <div className="flex justify-center">
                                            <FaFileAlt 
                                                className="text-gray-400 hover:text-gray-600 cursor-pointer transition text-lg" 
                                                onClick={() => handleActionClick(item)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                    <div className="text-sm font-normal text-gray-500">
                        Showing {filteredData.length === 0 ? "0 to 0 of 0" : `1 to ${filteredData.length} of ${filteredData.length}`} entries
                    </div>
                    <div className="flex border border-gray-200 rounded overflow-hidden">
                        <button className="px-4 py-2 text-sm font-bold text-gray-400 bg-gray-50/50" disabled>Previous</button>
                        <button className="px-4 py-2 text-sm font-bold text-gray-400 bg-gray-50/50" disabled>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditEngineerChallanList;
